import type { AccessLevel, AccessLevelProp, ButtonProps, ControlSize } from '@meterup/atto';
import type { Dispatch, ReactNode } from 'react';
import {
  Alert,
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  devices,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  HStack,
  Icon,
  isAccessLevelVisible,
  Segments,
  Small,
  SpecialKey,
  styled,
  Text,
  TextInput,
  Tooltip,
  useAccessLevelContext,
  useControlSize,
  VStack,
} from '@meterup/atto';
import { type DurationLike, DateTime, Duration } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import type { Timestamp } from '../../topics/time';
import type { DateTimeRangeBounds, DateTimeRangeSnapshot } from './DateTimeRangeStorageContext';
import type { SavedDateTimeRange } from './SavedDateTimeRanges';
import type { DailyTimeWindow, IntervalUnitsProp } from './utils';
import { checkDefinedOrThrow } from '../../helpers/expectDefinedOrThrow';
import { useTimestampDisplayContext } from '../../providers/TimestampDisplayContext';
import {
  formatDateTime,
  now,
  parseDateTime,
  stripDateForInput,
  stripDateTimeForInput,
  TimeFormat,
} from '../../topics/time';
import { FormatDateTime } from '../FormatDateTime';
import { useDateTimeRangeStateContextOrNull } from './DateTimeRangeStateContext';
import { useDateTimeRangeValue } from './DateTimeRangeStorageContext';
import {
  findSavedDateTimeRange,
  formatSavedDateTimeRange,
  SavedDateTimeRangesMenuGroup,
  useSavedDateTimeRangesOrNull,
  useSavedStartingDateTimeRanges,
} from './SavedDateTimeRanges';
import {
  formatDateTimeRangeLength,
  formatToISO,
  getIntervalDuration,
  getStep as defaultGetStep,
  IntervalUnits,
  intervalUnitsToSeconds,
  TimePeriod,
  timePeriodLabel,
} from './utils';

export interface UseDateTimeRangePickerProps {
  defaultValue?: TimePeriod;
  defaultStart?: Timestamp;
  defaultEnd?: Timestamp;
  intervalUnits?: IntervalUnits;
  allowCustom?: boolean | AccessLevel | null;
  fixAnchorToNow?: boolean;
  relativeTimeDirection?: 'future' | 'past';
  maxDuration?: DurationLike;
  stepSeconds?: number;
  getStep?: (value: number) => number;
}

export interface DateTimeRangeState extends UseDateTimeRangePickerProps {
  startDate: string; // ISO string start of range
  endDate: string; // ISO string end of range
  value: TimePeriod; // The selected time period
  /** @deprecated Use `intervalDuration` instead. */
  interval: number; // Total range duration in intervalUnits
  step: number;
  setTimePeriod: (value: TimePeriod) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  /** Hours of each day a custom range keeps, which charts leave the rest out of. */
  dailyWindow?: DailyTimeWindow | null;
  refreshNow: () => void;
  reset: () => void;

  previousTimeRange: DateTimeRangeSnapshot | null;
  /** The range last committed with `commitZoom`, which a range finder shows as its full extent. */
  zoomedTimeRange: DateTimeRangeBounds | null;
  /** Narrows to a range within the zoom level. `commit` also makes it the zoom level, in one step. */
  zoomToRange: (start: Date, end: Date, options?: { commit?: boolean }) => void;
  /** Commits the zoomed range as the new zoom level. `resetZoom` still restores `previousTimeRange`. */
  commitZoom: () => void;
  resetZoom: () => void;

  intervalUnits: IntervalUnits;
  fixAnchorToNow: boolean;
  allowCustom?: boolean | AccessLevel | null;
  relativeTimeDirection: 'future' | 'past';
  /** The pinned saved range, when this picker can show it and so started on it. */
  pinnedRange?: SavedDateTimeRange | null;

  /** End time if custom date range, or null if a preset range ending at now. */
  endTime: string | undefined;
  duration: Duration;
  /** Whole units, rounded up, so they're safe to send as GraphQL `Int` lookbacks. */
  getIntervalDuration: (units: IntervalUnits) => number;
  /** Whole units, rounded up, so they're safe to send as GraphQL `Int` lookbacks. */
  intervalDuration: Record<IntervalUnitsProp, number>;
}

export function useDateTimeRangePicker({
  defaultValue: defaultValueProp = TimePeriod.OneHour,
  defaultStart: defaultStartProp,
  defaultEnd: defaultEndProp,
  intervalUnits = IntervalUnits.Seconds,
  allowCustom = null,
  fixAnchorToNow = true,
  relativeTimeDirection = 'past',
  maxDuration,
  stepSeconds,
  getStep = defaultGetStep,
}: UseDateTimeRangePickerProps = {}): DateTimeRangeState {
  const { pinnedRange, pageDefaultRange } = useSavedStartingDateTimeRanges({
    allowCustom,
    fixAnchorToNow,
    maxDuration,
    relativeTimeDirection,
  });
  const savedRange = pinnedRange ?? pageDefaultRange;
  const defaultValue = savedRange ? TimePeriod.Custom : defaultValueProp;
  const resolvedDefaultStart =
    savedRange?.start ??
    defaultStartProp ??
    now().minus(
      defaultValueProp === TimePeriod.Custom ? { days: 1 } : { seconds: defaultValueProp },
    );
  const resolvedDefaultEnd = savedRange?.end ?? defaultEndProp ?? now();

  const defaultStart = useMemo(
    () => stripDateTimeForInput(parseDateTime(resolvedDefaultStart)!)!,
    [resolvedDefaultStart],
  );
  const defaultEnd = useMemo(
    () => stripDateTimeForInput(parseDateTime(resolvedDefaultEnd)!)!,
    [resolvedDefaultEnd],
  );

  const [timePeriod, setTimePeriod] = useDateTimeRangeValue('timePeriod', defaultValue);

  const [startDate, setStartDate] = useDateTimeRangeValue('startDate', defaultStart);
  const [endDate, setEndDate] = useDateTimeRangeValue('endDate', defaultEnd);
  const [previousTimeRange, setPreviousTimeRange] = useDateTimeRangeValue(
    'previousTimeRange',
    null,
  );
  const [zoomedTimeRange, setZoomedTimeRange] = useDateTimeRangeValue('zoomedTimeRange', null);
  const [anchorNow, setAnchorNow] = useState(() => now().startOf('minute'));

  useEffect(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [timePeriod]);

  const refreshNow = useCallback(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, []);

  const { calculatedStartDate, calculatedEndDate } = useMemo(() => {
    if (typeof timePeriod === 'number') {
      if (relativeTimeDirection === 'future') {
        return {
          calculatedStartDate: formatToISO(anchorNow)!,
          calculatedEndDate: formatToISO(anchorNow.plus({ seconds: timePeriod }))!,
        };
      }
      return {
        calculatedStartDate: formatToISO(anchorNow.minus({ seconds: timePeriod }))!,
        calculatedEndDate: formatToISO(anchorNow)!,
      };
    }

    let startISO = DateTime.fromISO(startDate);
    let endISO = DateTime.fromISO(endDate);
    if (fixAnchorToNow) {
      if (relativeTimeDirection === 'past') {
        endISO = DateTime.min(endISO, anchorNow);
      } else {
        startISO = DateTime.max(startISO, anchorNow);
      }
    }
    return {
      calculatedStartDate: formatToISO(startISO)!,
      calculatedEndDate: formatToISO(endISO)!,
    };
  }, [timePeriod, startDate, endDate, fixAnchorToNow, anchorNow, relativeTimeDirection]);

  const interval = useMemo(() => {
    if (timePeriod === TimePeriod.Custom) {
      if (!allowCustom) return getIntervalDuration(defaultValue, intervalUnits);

      const startDateCalc = DateTime.fromISO(calculatedStartDate);
      const endDateCalc = DateTime.fromISO(calculatedEndDate);
      const diff = endDateCalc.diff(startDateCalc, [intervalUnits]);
      return Math.round(diff.toObject()[intervalUnits] ?? 0);
    }

    return getIntervalDuration(timePeriod, intervalUnits);
  }, [
    calculatedStartDate,
    calculatedEndDate,
    timePeriod,
    allowCustom,
    defaultValue,
    intervalUnits,
  ]);

  const clampedInterval = useMemo(() => {
    if (maxDuration == null) {
      return interval;
    }

    return Math.min(
      getIntervalDuration(
        Duration.fromDurationLike(maxDuration).toMillis(),
        IntervalUnits.Milliseconds,
      ),
      interval,
    );
  }, [interval, maxDuration]);
  const step = stepSeconds ?? getStep(clampedInterval);

  const reset = useCallback(() => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setTimePeriod(defaultValue);
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [
    setStartDate,
    setEndDate,
    setTimePeriod,
    setAnchorNow,
    defaultStart,
    defaultEnd,
    defaultValue,
  ]);

  const zoomToRange = useCallback(
    (start: Date, end: Date, { commit = false }: { commit?: boolean } = {}) => {
      setPreviousTimeRange(
        (prev) =>
          prev ?? {
            timePeriod,
            startDate: calculatedStartDate,
            endDate: calculatedEndDate,
          },
      );
      const nextStart = parseDateTime(start)?.toISO();
      const nextEnd = parseDateTime(end)?.toISO();
      if (!nextStart || !nextEnd) return;
      setStartDate(nextStart);
      setEndDate(nextEnd);
      setTimePeriod(TimePeriod.Custom);
      if (commit) setZoomedTimeRange({ startDate: nextStart, endDate: nextEnd });
    },
    [
      timePeriod,
      calculatedStartDate,
      calculatedEndDate,
      setPreviousTimeRange,
      setStartDate,
      setEndDate,
      setTimePeriod,
      setZoomedTimeRange,
    ],
  );

  const commitZoom = useCallback(() => {
    if (previousTimeRange == null) return;
    setZoomedTimeRange({ startDate: calculatedStartDate, endDate: calculatedEndDate });
  }, [previousTimeRange, calculatedStartDate, calculatedEndDate, setZoomedTimeRange]);

  const resetZoom = useCallback(() => {
    if (previousTimeRange == null) return;
    setStartDate(previousTimeRange.startDate);
    setEndDate(previousTimeRange.endDate);
    setTimePeriod(previousTimeRange.timePeriod);
    setPreviousTimeRange(null);
    setZoomedTimeRange(null);
  }, [
    previousTimeRange,
    setStartDate,
    setEndDate,
    setTimePeriod,
    setPreviousTimeRange,
    setZoomedTimeRange,
  ]);

  useEffect(() => {
    if (timePeriod === TimePeriod.Custom) return;
    if (previousTimeRange != null) setPreviousTimeRange(null);
    if (zoomedTimeRange != null) setZoomedTimeRange(null);
  }, [timePeriod, previousTimeRange, setPreviousTimeRange, zoomedTimeRange, setZoomedTimeRange]);

  return useMemo(() => {
    const seconds = clampedInterval * intervalUnitsToSeconds[intervalUnits];
    const wholeUnits = (units: IntervalUnits) => Math.ceil(getIntervalDuration(seconds, units));
    return {
      defaultValue,
      defaultStart,
      defaultEnd,
      intervalUnits,
      allowCustom,
      fixAnchorToNow,
      relativeTimeDirection,
      pinnedRange,
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      value: timePeriod,
      interval: clampedInterval,
      step,
      setStartDate,
      setEndDate,
      setTimePeriod,
      reset,
      refreshNow,
      previousTimeRange,
      zoomedTimeRange,
      zoomToRange,
      commitZoom,
      resetZoom,
      maxDuration,
      get endTime() {
        return timePeriod === TimePeriod.Custom ? calculatedEndDate : undefined;
      },
      get duration() {
        return Duration.fromDurationLike({ [intervalUnits]: clampedInterval });
      },
      getIntervalDuration: wholeUnits,
      intervalDuration: {
        seconds,
        get milliseconds() {
          return wholeUnits(IntervalUnits.Milliseconds);
        },
        get minutes() {
          return wholeUnits(IntervalUnits.Minutes);
        },
        get hours() {
          return wholeUnits(IntervalUnits.Hours);
        },
      },
    };
  }, [
    clampedInterval,
    intervalUnits,
    defaultValue,
    defaultStart,
    defaultEnd,
    allowCustom,
    fixAnchorToNow,
    relativeTimeDirection,
    pinnedRange,
    calculatedStartDate,
    calculatedEndDate,
    timePeriod,
    step,
    setStartDate,
    setEndDate,
    setTimePeriod,
    reset,
    refreshNow,
    previousTimeRange,
    zoomedTimeRange,
    zoomToRange,
    commitZoom,
    resetZoom,
    maxDuration,
  ]);
}

/** Whether the range is narrower than its zoom level, so `commitZoom` has somewhere to go. */
export function canCommitZoom({
  startDate,
  endDate,
  previousTimeRange,
  zoomedTimeRange,
}: Pick<
  DateTimeRangeState,
  'startDate' | 'endDate' | 'previousTimeRange' | 'zoomedTimeRange'
>): boolean {
  const level = zoomedTimeRange ?? previousTimeRange;
  if (level == null) return false;
  return (
    Date.parse(startDate) > Date.parse(level.startDate) ||
    Date.parse(endDate) < Date.parse(level.endDate)
  );
}

type TimePeriodOption = { accessLevel?: AccessLevelProp | null | undefined; value: TimePeriod };

type ChildrenOrOptions =
  | {
      children: ReactNode;
      options?: never;
    }
  | {
      children?: never;
      options: (TimePeriod | TimePeriodOption)[];
    };

type DateTimeRangePickerDropdownContentsProps = {
  state: DateTimeRangeState;
  /** @deprecated Use `dateTimeRangeState.maxDuration` instead */
  maxDuration?: DurationLike;
  onValueChange?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  setIsOpen?: Dispatch<boolean>;
  /**
   * Closes the picker and runs `then` once it has finished closing, so a dialog keeps its focus and
   * a range change that suspends the page can't leave the portaled menu showing over its fallback.
   */
  closeThen?: (then: () => void) => void;
  timeZone?: string;
  /** Only show the custom range inputs, without the relative time-period presets. */
  hidePresets?: boolean;
} & ChildrenOrOptions;

export type DateTimeRangePickerProps = Omit<DateTimeRangePickerDropdownContentsProps, 'state'> & {
  /**
   * Hides the label while a container query matches, leaving the calendar icon, as on `Button`. On by
   * default; ignored with `zoomControls`, whose fixed width is already compact.
   */
  condense?: ButtonProps['condense'];
  state?: DateTimeRangeState;
  size?: ControlSize;
  tooltip?: ReactNode;
  width?: ButtonProps['width'];
  /**
   * Shows buttons to zoom into and reset a custom range beside the picker, for pairing with a
   * timeline. The picker and buttons share one fixed width, so the group never resizes. Without
   * it, a zoomed range still gets a button to reset it.
   */
  zoomControls?: boolean;
} & ChildrenOrOptions;

function normalizeOptions(options: (TimePeriod | TimePeriodOption)[]): TimePeriodOption[] {
  return options
    .map((option) => (typeof option === 'object' ? option : { value: option }))
    .filter((option) => option.accessLevel !== null);
}

const RangeLabelHyphen = styled(Text, {
  color: colors.gray200,

  [darkThemeSelector]: {
    color: colors.gray400,
  },
});
RangeLabelHyphen.displayName = 'RangeLabelHyphen';

type PickerSegment = 'presets' | 'custom';

const DateTimeRangeFields = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$6',
});
DateTimeRangeFields.displayName = 'DateTimeRangeFields';

// Tints the whole section edge to edge; the extra padding keeps the fields where a card would.
const DateTimeRangeGroup = styled(DropdownMenuGroup, {
  background: colors.bgSurfaceLight,

  [darkThemeSelector]: {
    background: colors.bgSurfaceDark,
  },

  [devices.touch]: {
    padding: '$12',
  },

  [devices.mouse]: {
    padding: '$10',
  },
});
DateTimeRangeGroup.displayName = 'DateTimeRangeGroup';

const DateTimeRangeRows = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',

  // Chrome's clock button doesn't fit beside the date at this width; typing and arrow keys remain.
  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    display: 'none',
  },
});
DateTimeRangeRows.displayName = 'DateTimeRangeRows';

const DateTimeRangeFieldLabel = styled('span', {
  minWidth: '$28',
});
DateTimeRangeFieldLabel.displayName = 'DateTimeRangeFieldLabel';

const DateTimeRangeLength = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  padding: '0 $8',
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  '&::before, &::after': {
    content: '""',
    flexGrow: 1,
    height: 1,
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },
});
DateTimeRangeLength.displayName = 'DateTimeRangeLength';

// Disabled buttons get no pointer events, so the tooltip listens on this wrapper instead. Unlike
// Tooltip's own fallback trigger it isn't a button, so it can't nest one.
const DisabledReasonTarget = styled('span', {
  display: 'flex',

  '& :disabled': {
    pointerEvents: 'none',
  },

  variants: {
    grow: {
      true: {
        flexGrow: 1,
      },
    },
  },
});
DisabledReasonTarget.displayName = 'DisabledReasonTarget';

function DisabledReasonTooltip({
  reason,
  grow = false,
  children,
}: {
  reason: string | null;
  grow?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip contents={reason} disabled={reason == null}>
      <DisabledReasonTarget grow={grow} tabIndex={reason != null ? 0 : undefined}>
        {children}
      </DisabledReasonTarget>
    </Tooltip>
  );
}

export function DateTimeRangePickerDropdownContents({
  state,
  children,
  maxDuration: deprecatedMaxDuration,
  options,
  onValueChange,
  // DASH-8517: For some reason the date/time inputs just completely ignore input if no `onKeyDown` property is being passed.
  // I don't know what caused this regression but this makes the inputs work again.
  onKeyDown = () => {},
  setIsOpen,
  closeThen,
  timeZone,
  hidePresets = false,
}: DateTimeRangePickerDropdownContentsProps) {
  const {
    setStartDate,
    setEndDate,
    setTimePeriod,
    refreshNow,
    startDate,
    endDate,
    interval,
    intervalUnits,
    value: dateTimeRangePickerValue,
    allowCustom,
    relativeTimeDirection,
    maxDuration,
  } = state;
  const saved = useSavedDateTimeRangesOrNull();
  const activeSavedRange =
    saved && allowCustom ? findSavedDateTimeRange(saved.ranges, state) : null;

  const normalizedOptions = useMemo(
    () => (options ? normalizeOptions(options) : undefined),
    [options],
  );

  const intervalSeconds = useMemo(
    () => intervalUnitsToSeconds[intervalUnits] * interval,
    [interval, intervalUnits],
  );

  useEffect(() => {
    if (
      dateTimeRangePickerValue === TimePeriod.Custom ||
      !normalizedOptions ||
      normalizedOptions.some((opt) => opt.value === intervalSeconds)
    )
      return;

    const descendingOptions = normalizedOptions
      .filter((o): o is typeof o & { value: number } => typeof o.value === 'number')
      .sort((a, b) => b.value - a.value);

    setTimePeriod(
      (descendingOptions.find((option) => option.value <= intervalSeconds) ?? normalizedOptions[0])
        .value,
    );
  }, [normalizedOptions, intervalSeconds, setTimePeriod, dateTimeRangePickerValue]);

  const [activeSegment, setActiveSegment] = useState<PickerSegment>(
    dateTimeRangePickerValue === TimePeriod.Custom && !activeSavedRange ? 'custom' : 'presets',
  );
  const effectiveSegment = hidePresets ? 'custom' : activeSegment;

  const [startDateLocal, setStartDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(startDate)!),
  );
  const [startTimeLocal, setStartTimeLocal] = useState<string>(
    formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']),
  );
  const [endDateLocal, setEndDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(endDate)!),
  );
  const [endTimeLocal, setEndTimeLocal] = useState<string>(
    formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']),
  );

  // Keep local inputs in sync when external values change (e.g., presets or navigation)
  useEffect(() => {
    setStartDateLocal(stripDateForInput(parseDateTime(startDate)!));
    setStartTimeLocal(formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']));
  }, [startDate]);
  useEffect(() => {
    setEndDateLocal(stripDateForInput(parseDateTime(endDate)!));
    setEndTimeLocal(formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']));
  }, [endDate]);

  const nextStart = useMemo(
    () =>
      DateTime.fromISO(`${startDateLocal}T${startTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [startDateLocal, startTimeLocal, timeZone],
  );

  const nextEnd = useMemo(
    () =>
      DateTime.fromISO(`${endDateLocal}T${endTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [endDateLocal, endTimeLocal, timeZone],
  );

  const rangeLength = useMemo(
    () => formatDateTimeRangeLength(nextStart, nextEnd),
    [nextStart, nextEnd],
  );

  const effectiveMaxDuration = maxDuration ?? deprecatedMaxDuration;

  const customErrorMessages = useMemo(() => {
    const errors = [];

    if (!nextStart.isValid) errors.push('Please provide a valid start date.');
    if (!nextEnd.isValid) errors.push('Please provide a valid end date.');
    if (nextStart.valueOf() > nextEnd.valueOf()) errors.push('End date must be after start date.');
    if (
      effectiveMaxDuration != null &&
      nextEnd.diff(nextStart).toMillis() >
        Duration.fromDurationLike(effectiveMaxDuration).toMillis()
    ) {
      errors.push(
        `Date range must be shorter than ${Duration.fromDurationLike(effectiveMaxDuration).toHuman()}.`,
      );
    }

    if (!errors.length) return null;

    return errors;
  }, [nextStart, nextEnd, effectiveMaxDuration]);

  const applyDisabledReason = useMemo(() => {
    if (customErrorMessages?.length) return 'Fix the range above to apply it.';

    const nextStartISO = nextStart.toISO();
    const nextEndISO = nextEnd.toISO();

    return nextStartISO === startDate && nextEndISO === endDate
      ? 'This range is already applied.'
      : null;
  }, [customErrorMessages?.length, endDate, nextEnd, nextStart, startDate]);
  const isDisabled = applyDisabledReason != null;

  const handleApply = useCallback(() => {
    if (!nextStart.isValid || !nextEnd.isValid) return;
    if (isDisabled) return;

    // validity checked above
    const nextStartISO = nextStart.toISO()!;
    const nextEndISO = nextEnd.toISO()!;

    const apply = () => {
      onValueChange?.();
      setStartDate(nextStartISO);
      setEndDate(nextEndISO);
      setTimePeriod(TimePeriod.Custom);
    };
    if (closeThen) {
      closeThen(apply);
    } else {
      apply();
      setIsOpen?.(false);
    }
  }, [
    nextStart,
    nextEnd,
    isDisabled,
    onValueChange,
    setStartDate,
    setEndDate,
    setTimePeriod,
    setIsOpen,
    closeThen,
  ]);

  const applySavedRange = useCallback(
    (range: SavedDateTimeRange) => {
      onValueChange?.();
      setStartDate(range.start);
      setEndDate(range.end);
      setTimePeriod(TimePeriod.Custom);
      refreshNow();
    },
    [onValueChange, setStartDate, setEndDate, setTimePeriod, refreshNow],
  );

  const saveDisabledReason = useMemo(() => {
    if (customErrorMessages?.length) return 'Fix the range above to save it.';
    const existing = saved?.ranges.find(
      (range) =>
        Date.parse(range.start) === nextStart.toMillis() &&
        Date.parse(range.end) === nextEnd.toMillis(),
    );
    return existing ? `Already saved as ${existing.name}.` : null;
  }, [customErrorMessages?.length, saved?.ranges, nextStart, nextEnd]);

  const handleSave = useCallback(() => {
    const start = nextStart.toISO();
    const end = nextEnd.toISO();
    if (!saved || !start || !end) return;
    const openDialog = () => saved.save({ start, end }, applySavedRange);
    if (closeThen) closeThen(openDialog);
    else openDialog();
  }, [saved, nextStart, nextEnd, applySavedRange, closeThen]);

  const saveButton = (
    <Button
      size="small"
      variant="secondary"
      appearance="muted"
      icon="save"
      arrangement="hidden-label"
      disabled={saveDisabledReason != null}
      onClick={handleSave}
    >
      Save as preset…
    </Button>
  );

  const applyButton = (
    <Button
      size="small"
      variant="brand"
      icon="checkmark-circle"
      arrangement="leading-icon"
      width={saved ? 'full' : undefined}
      disabled={isDisabled}
      onClick={handleApply}
    >
      Apply
    </Button>
  );

  return (
    <>
      {allowCustom && !hidePresets && (
        <DropdownMenuGroup>
          <Segments
            proportions="equal"
            size="small"
            segments={[
              {
                label: 'Presets',
                selected: activeSegment === 'presets',
                onClick: () => setActiveSegment('presets'),
              },
              {
                label: 'Range',
                accessLevel: typeof allowCustom !== 'boolean' ? allowCustom : undefined,
                selected: activeSegment === 'custom',
                onClick: () => setActiveSegment('custom'),
              },
            ]}
          />
        </DropdownMenuGroup>
      )}
      {(!allowCustom || effectiveSegment === 'presets') && (
        <SavedDateTimeRangesMenuGroup
          state={state}
          onApply={applySavedRange}
          closeThen={closeThen}
        />
      )}
      {(!allowCustom || effectiveSegment === 'presets') && (
        <DropdownMenuRadioGroup
          label={`Filter by ${relativeTimeDirection}`}
          value={dateTimeRangePickerValue.toString()}
          onValueChange={(val: string) => {
            let value = TimePeriod.OneHour;
            if (val === TimePeriod.Custom) {
              value = val;
            }
            const num = Number.parseInt(val, 10);
            if (
              !Number.isNaN(num) &&
              Number.isInteger(num) &&
              Object.values(TimePeriod).includes(num)
            ) {
              value = num;
            }
            if (closeThen) closeThen(() => setTimePeriod(value));
            else setTimePeriod(value);
          }}
        >
          {children ||
            normalizedOptions?.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value.toString()}
                accessLevel={option.accessLevel}
                closeOnClick
                onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLDivElement> | undefined}
              >
                {timePeriodLabel(option.value)}
              </DropdownMenuRadioItem>
            ))}
        </DropdownMenuRadioGroup>
      )}
      {allowCustom && effectiveSegment === 'custom' && (
        <>
          <DateTimeRangeGroup>
            <DateTimeRangeFields>
              <DateTimeRangeRows>
                <ControlGroup direction="row" size="small" width="full">
                  <TextInput
                    type="date"
                    aria-label="Start date"
                    prefix={<DateTimeRangeFieldLabel>Start</DateTimeRangeFieldLabel>}
                    onChange={setStartDateLocal}
                    value={startDateLocal}
                    width="100%"
                    minWidth={0}
                    onKeyDown={onKeyDown}
                  />
                  <TextInput
                    type="time"
                    aria-label="Start time"
                    onChange={setStartTimeLocal}
                    value={startTimeLocal}
                    onKeyDown={onKeyDown}
                  />
                </ControlGroup>
                <DateTimeRangeLength>
                  <Icon icon="arrow-down" size={10} variant="neutral" />
                  {rangeLength && <Small>{rangeLength}</Small>}
                </DateTimeRangeLength>
                <ControlGroup direction="row" size="small" width="full">
                  <TextInput
                    type="date"
                    aria-label="End date"
                    prefix={<DateTimeRangeFieldLabel>End</DateTimeRangeFieldLabel>}
                    onChange={setEndDateLocal}
                    value={endDateLocal}
                    width="100%"
                    minWidth={0}
                    onKeyDown={onKeyDown}
                  />
                  <TextInput
                    type="time"
                    aria-label="End time"
                    onChange={setEndTimeLocal}
                    value={endTimeLocal}
                    onKeyDown={onKeyDown}
                  />
                </ControlGroup>
              </DateTimeRangeRows>
              {!!customErrorMessages?.length && (
                <Alert
                  type="inline"
                  variant="negative"
                  copy={
                    <VStack spacing={4}>
                      {customErrorMessages.map((err, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Text key={`${index}:${err}`}>{err}</Text>
                      ))}
                    </VStack>
                  }
                />
              )}
            </DateTimeRangeFields>
          </DateTimeRangeGroup>
          <DropdownMenuGroup>
            {saved ? (
              <HStack spacing={6}>
                <DisabledReasonTooltip reason={saveDisabledReason}>
                  {saveButton}
                </DisabledReasonTooltip>
                <DisabledReasonTooltip reason={applyDisabledReason} grow>
                  {applyButton}
                </DisabledReasonTooltip>
              </HStack>
            ) : (
              <DisabledReasonTooltip reason={applyDisabledReason}>
                {applyButton}
              </DisabledReasonTooltip>
            )}
          </DropdownMenuGroup>
        </>
      )}
    </>
  );
}

// "Custom" beside the two icon buttons, measured per size and rounded up to the spacing scale.
// That's wider than any preset label alone, so the picker holds one width across every range.
// Access-level badges widen every button, hence the separate badged widths.
const ZoomControlsContainer = styled('div', {
  flexShrink: 0,

  variants: {
    size: {
      'x-small': { width: '$132', [devices.touch]: { width: '$136' } },
      small: { width: '$148', [devices.touch]: { width: '$172' } },
      medium: { width: '$164', [devices.touch]: { width: '$184' } },
      large: { width: '$204', [devices.touch]: { width: '$212' } },
      'x-large': { width: '$252', [devices.touch]: { width: '$260' } },
    },
    badged: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      size: 'x-small',
      badged: true,
      css: { width: '$188', [devices.touch]: { width: '$208' } },
    },
    {
      size: 'small',
      badged: true,
      css: { width: '$216', [devices.touch]: { width: '$260' } },
    },
    {
      size: 'medium',
      badged: true,
      css: { width: '$232', [devices.touch]: { width: '$276' } },
    },
    {
      size: 'large',
      badged: true,
      css: { width: '$276', [devices.touch]: { width: '$296' } },
    },
    {
      size: 'x-large',
      badged: true,
      css: { width: '$336', [devices.touch]: { width: '$356' } },
    },
  ],
});
ZoomControlsContainer.displayName = 'ZoomControlsContainer';

function DateTimeRangeZoomControls({ state }: { state: DateTimeRangeState }) {
  const canZoom = canCommitZoom(state);
  const reset = state.previousTimeRange ? state.resetZoom : state.reset;

  // Option, not Cmd: browsers reserve Cmd/Ctrl+0 for resetting page zoom.
  useHotkeys('alt+z', state.commitZoom, { enabled: canZoom, preventDefault: true }, [
    state.commitZoom,
  ]);
  useHotkeys('alt+0', reset, { preventDefault: true }, [reset]);

  const zoomButton = (
    <Button
      appearance="muted"
      arrangement="hidden-label"
      disabled={!canZoom}
      icon="zoom-in"
      onClick={state.commitZoom}
      shortcut={[SpecialKey.Alt, 'Z']}
      minWidth="max-content"
      variant="secondary"
      width="auto"
    >
      Zoom timeline to selection
    </Button>
  );

  return (
    <>
      {canZoom ? (
        zoomButton
      ) : (
        <Tooltip
          asChild={false}
          contents="Drag the timeline handles to select a narrower range to zoom into."
        >
          {zoomButton}
        </Tooltip>
      )}
      <Button
        appearance="muted"
        arrangement="hidden-label"
        icon="reset"
        onClick={reset}
        shortcut={[SpecialKey.Alt, '0']}
        minWidth="max-content"
        variant="secondary"
        width="auto"
      >
        Reset timeline range
      </Button>
    </>
  );
}

function formatRangeLabel(state: DateTimeRangeState, zone: string | undefined): string {
  if (state.value !== TimePeriod.Custom) return timePeriodLabel(state.value);
  return `${formatDateTime(state.startDate, TimeFormat.MEDIUM, zone)} – ${formatDateTime(state.endDate, TimeFormat.MEDIUM, zone)}`;
}

function DateTimeRangeZoomResetButton({ state }: { state: DateTimeRangeState }) {
  useHotkeys('alt+0', state.resetZoom, { preventDefault: true }, [state.resetZoom]);

  return (
    <Button
      appearance="muted"
      arrangement="hidden-label"
      icon="reset"
      onClick={state.resetZoom}
      shortcut={[SpecialKey.Alt, '0']}
      variant="secondary"
    >
      Reset zoom
    </Button>
  );
}

export function DateTimeRangePicker({
  condense: condenseProp = true,
  size,
  tooltip,
  width,
  zoomControls = false,

  state: stateProp,

  ...props
}: DateTimeRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const afterCloseRef = useRef<(() => void) | null>(null);

  const closeThen = useCallback((then: () => void) => {
    afterCloseRef.current = then;
    setIsOpen(false);
  }, []);

  const handleOpenChangeComplete = useCallback((open: boolean) => {
    if (open || !afterCloseRef.current) return;
    const then = afterCloseRef.current;
    afterCloseRef.current = null;
    then();
  }, []);

  const timestampDisplay = useTimestampDisplayContext();
  const stateContext = useDateTimeRangeStateContextOrNull();
  const controlSize = useControlSize(size);
  const accessLevel = useAccessLevelContext();

  const state = checkDefinedOrThrow(
    stateProp ?? stateContext,
    new Error('DateTimeRangePicker must be provided a state or used in a DateTimeRangeProvider'),
  );
  const isCustom = state.value === TimePeriod.Custom;
  const condense = zoomControls ? false : condenseProp;
  const saved = useSavedDateTimeRangesOrNull();
  const activeSavedRange =
    saved && state.allowCustom ? findSavedDateTimeRange(saved.ranges, state) : null;
  const rangeLabel = condense
    ? activeSavedRange?.name ?? formatRangeLabel(state, timestampDisplay?.displayZone)
    : undefined;
  const triggerTooltip =
    tooltip ?? (activeSavedRange ? formatSavedDateTimeRange(activeSavedRange) : undefined);

  const trigger = (
    <DropdownMenuButton
      appearance="muted"
      arrangement="leading-icon"
      // Button names a condensed control by stringifying its children, which here are elements.
      aria-label={rangeLabel}
      callout={
        isCustom ? { variant: 'brand', label: 'Custom range', condensedOnly: true } : undefined
      }
      condense={condense}
      icon="calendar"
      size={size}
      variant="secondary"
      width={zoomControls ? undefined : width}
    >
      {activeSavedRange?.name}
      {isCustom && !activeSavedRange && zoomControls && 'Custom'}
      {isCustom && !activeSavedRange && !zoomControls && (
        <Text>
          <FormatDateTime value={state.startDate} format={TimeFormat.MEDIUM} />{' '}
          <RangeLabelHyphen>–</RangeLabelHyphen>{' '}
          <FormatDateTime value={state.endDate} format={TimeFormat.MEDIUM} />
        </Text>
      )}
      {!isCustom && timePeriodLabel(state.value)}
    </DropdownMenuButton>
  );

  const picker = (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={handleOpenChangeComplete}
      modal={false}
    >
      {/* Always wrapped, as adding it later would remount the trigger under the open menu. With
          contents, it replaces Button's condensed tooltip, so it carries the range too. */}
      <Tooltip
        contents={
          rangeLabel && triggerTooltip ? (
            <VStack spacing={2}>
              <Text>{rangeLabel}</Text>
              {triggerTooltip}
            </VStack>
          ) : (
            triggerTooltip
          )
        }
        disabled={!triggerTooltip}
      >
        {trigger}
      </Tooltip>
      <DropdownMenuPopover align="start" side="bottom" sideOffset={6} width={280}>
        <DateTimeRangePickerDropdownContents
          state={state}
          setIsOpen={setIsOpen}
          closeThen={closeThen}
          {...props}
        />
      </DropdownMenuPopover>
    </DropdownMenu>
  );

  if (!zoomControls) {
    if (state.previousTimeRange == null) return picker;
    return (
      <ControlGroup relation="joined" size={controlSize}>
        {picker}
        <DateTimeRangeZoomResetButton state={state} />
      </ControlGroup>
    );
  }

  return (
    <ZoomControlsContainer
      size={controlSize}
      badged={accessLevel != null && isAccessLevelVisible(accessLevel)}
    >
      <ControlGroup relation="joined" size={controlSize} width="full">
        {picker}
        {isCustom && <DateTimeRangeZoomControls state={state} />}
      </ControlGroup>
    </ZoomControlsContainer>
  );
}
